import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import type { MutableRefObject } from "react";
import "./U2000AxiosRequestConfig";
import { U2000ParseResponseError } from "./U2000Axios";

/**
 * Ref globale du token, injectée par AuthContext au démarrage.
 * Permet à l'intercepteur Axios de lire le token le plus récent sans dépendre de useEffect.
 */
export let globalTokenRef: MutableRefObject<string | null> | null = null;

/**
 * Callbacks globaux pour le busy indicator et le logout.
 * Injectés par le hook useU2000AxiosAuthenticated au premier montage.
 */
let showBusyIndicatorFn: (() => void) | null = null;
let hideBusyIndicatorFn: (() => void) | null = null;
let logoutFn: (() => void) | null = null;
/** Retourne l'auth mise à jour (au minimum { token }); utilisé par le retry 403 pour le nouveau token. */
let doRefreshTokenFn: ((data: any) => Promise<{ token?: string | null }>) | null = null;

/**
 * Configure la ref globale du token (appelé une seule fois par AuthContext).
 */
export function setGlobalTokenRef(ref: MutableRefObject<string | null>) {
  globalTokenRef = ref;
}

/**
 * Configure les callbacks globaux (appelé une seule fois par le premier composant qui utilise useU2000AxiosAuthenticated).
 */
export function setGlobalCallbacks(
  showBusy: () => void,
  hideBusy: () => void,
  logout: () => void,
  doRefreshToken: (data: any) => Promise<{ token?: string | null }>
) {
  showBusyIndicatorFn = showBusy;
  hideBusyIndicatorFn = hideBusy;
  logoutFn = logout;
  doRefreshTokenFn = doRefreshToken;
}

/**
 * Configure les intercepteurs Axios (appelé une seule fois au démarrage de l'app).
 */
export function setupAxiosInterceptors(axiosInstance: AxiosInstance) {
  if (import.meta.env.DEV) {
    console.debug("[Axios] setupAxiosInterceptors - configuration des intercepteurs");
  }

  // Request interceptor
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!config.skipBusyIndicator && showBusyIndicatorFn) showBusyIndicatorFn();

      if (import.meta.env.DEV) {
        console.debug(
          `(AXIOS) : ${config.method?.toUpperCase() ?? "UNKNOWN"} request sent to ${config.url} ` +
            `${config.params ? `(with params ${JSON.stringify(config.params)})` : ""}` +
            `${config.data ? `(with data ${JSON.stringify(config.data)})` : ""}`
        );
      }

      // Lire tokenRef global à la demande → token toujours à jour (useLayoutEffect dans AuthContext)
      if (config.headers && !config.headers["Authorization"]) {
        const token = globalTokenRef?.current;
        if (import.meta.env.DEV && token) {
          console.debug(`[Axios] Token injecté pour ${config.url}`);
        }
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        } else if (import.meta.env.DEV) {
          console.warn("[Axios] Token null, requête peut échouer (401/403)");
        }
      }
      return config;
    },
    (error: any) => {
      const config = error?.config as InternalAxiosRequestConfig | undefined;
      if (!config?.skipBusyIndicator && hideBusyIndicatorFn) hideBusyIndicatorFn();
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (import.meta.env.DEV) {
        console.debug(
          `(AXIOS) : ${response.config.method?.toUpperCase()} response from ${response.config.url} ` +
            `with status code ${response.status} and Data ${JSON.stringify(response.data)}`
        );
      }
      if (!response.config.skipBusyIndicator && hideBusyIndicatorFn) hideBusyIndicatorFn();
      return response;
    },
    async (error: AxiosError) => {
      const prevRequest = error?.config as (InternalAxiosRequestConfig & { sent?: boolean }) | undefined;

      // Retry sur 403 avec refresh token
      if (error?.response?.status === 403 && prevRequest && !prevRequest.sent) {
        prevRequest.sent = true;
        try {
          if (!doRefreshTokenFn) throw new Error("doRefreshToken not configured");
          // Utiliser le token retourné par le refresh : globalTokenRef n'est mis à jour qu'au
          // prochain useLayoutEffect (après commit React), donc serait encore l'ancien ici.
          const result = await doRefreshTokenFn(null);
          const refreshedToken = (result as { token?: string | null })?.token ?? globalTokenRef?.current;
          if (prevRequest.headers && refreshedToken) {
            prevRequest.headers["Authorization"] = `Bearer ${refreshedToken}`;
          }
          return axiosInstance(prevRequest);
        } catch (refreshError) {
          if (!prevRequest.skipBusyIndicator && hideBusyIndicatorFn) hideBusyIndicatorFn();
          if (logoutFn) logoutFn();
          return Promise.reject(refreshError);
        }
      }

      const skipBusy = prevRequest?.skipBusyIndicator === true;
      if (error.response) {
        const u2000Error = U2000ParseResponseError(error);
        if (import.meta.env.DEV) console.debug(`(AXIOS RESPONSE ERROR) : ${JSON.stringify(u2000Error)}`);
        if (!skipBusy && hideBusyIndicatorFn) hideBusyIndicatorFn();
        return Promise.reject(u2000Error);
      } else if (error.request) {
        if (!skipBusy && hideBusyIndicatorFn) hideBusyIndicatorFn();
        console.error(error.request);
      } else {
        if (!skipBusy && hideBusyIndicatorFn) hideBusyIndicatorFn();
        console.error("Error", error.message);
      }
      return Promise.reject(error);
    }
  );
}
